/**
 * Sign-in link — `https://pos.matix.one/?tenant=<slug>&pin=<password>[&lang=ar|en]`.
 *
 * The matixerp.com «جرّب نقاط البيع» buttons open the till already signed in
 * to a demo company, the way the old POS's `Auth/Login?domain=…&password=…`
 * links did. The link carries the same two things the login form asks for; the
 * normal login runs with them, and the existing auto-skips (one company, one
 * branch with a drawer, first drawer) take the visitor straight to the till.
 *
 * Read once, at import, and removed from the address bar immediately, so the
 * password does not stay in the URL, the history entry, or a bookmark. A link
 * always wins over a stored session: someone who opens the restaurant demo and
 * then the salon demo must land in the salon, not in the restaurant they were
 * already signed in to.
 */
import { createStorage } from '@matix/utils';

export interface SignInLink {
  tenant: string;
  password: string;
}

/** The language store `lib/language.tsx` reads at mount (same namespace and key). */
const LANG_STORE = createStorage('pos:cashier');
const LANG_KEY = 'login_lang';

export function readSignInLink(href: string): { link: SignInLink | null; cleaned: string | null; lang: 'ar' | 'en' | null } {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return { link: null, cleaned: null, lang: null };
  }
  const tenant = url.searchParams.get('tenant')?.trim() ?? '';
  const password = url.searchParams.get('pin') ?? '';
  const langParam = url.searchParams.get('lang');
  // `lang` is honoured only as part of a sign-in link, so an ordinary address stays untouched.
  if (!url.searchParams.has('tenant') && !url.searchParams.has('pin')) return { link: null, cleaned: null, lang: null };
  const lang = langParam === 'ar' || langParam === 'en' ? langParam : null;
  url.searchParams.delete('tenant');
  url.searchParams.delete('pin');
  url.searchParams.delete('lang');
  const cleaned = url.pathname + url.search + url.hash;
  if (!tenant || !password) return { link: null, cleaned, lang };
  return { link: { tenant, password }, cleaned, lang };
}

let pending: SignInLink | null = null;
if (typeof window !== 'undefined') {
  const { link, cleaned, lang } = readSignInLink(window.location.href);
  // Written before the LanguageProvider mounts, so the till opens in the site's language.
  if (lang) {
    try {
      LANG_STORE.set(LANG_KEY, lang);
    } catch {
      /* storage unavailable — the till opens in its default language */
    }
  }
  if (cleaned !== null) {
    try {
      window.history.replaceState(window.history.state, '', cleaned);
    } catch {
      /* history unavailable — the link still signs in */
    }
  }
  pending = link;
}

/** True while a sign-in link is waiting to be used. */
export function hasSignInLink(): boolean {
  return pending !== null;
}

/** Hands the link over exactly once. */
export function takeSignInLink(): SignInLink | null {
  const link = pending;
  pending = null;
  return link;
}
